import { Provider, Type, NgZone } from '@angular/core';
import { TestBed, waitForAsync } from '@angular/core/testing';
import { ActivatedRoute, Router } from '@angular/router';

import { setProps } from '../internals/query';
import * as customMatchers from '../matchers';
import { SpecOverrides, overrideComponentIfProviderOverridesSpecified, overrideModules } from '../Spec/create-factory';
import { addMatchers } from '../core';
import { isType } from '../types';

import { ActivatedRouteStub } from './activated-route-stub';
import { initialRoutingModule } from './initial-module';
import { getRoutingDefaultOptions, SpecRoutingOptions } from './options';
import { RouteOptions } from './route-options';
import { SpecRouting } from './spec-routing';
import { SpyObject } from '../mock';

/**
 * @publicApi
 */
export type SpecRoutingOverrides<C> = SpecOverrides<C> & RouteOptions;

/**
 * @publicApi
 */
export type SpecRoutingFactory<C> = (options?: SpecRoutingOverrides<C>) => SpecRouting<C>;

/**
 * @publicApi
 */
export function createRoutingFactory<C>(typeOrOptions: Type<C> | SpecRoutingOptions<C>): SpecRoutingFactory<C> {
  const options = isType(typeOrOptions)
    ? getRoutingDefaultOptions<C>({ component: typeOrOptions })
    : getRoutingDefaultOptions(typeOrOptions);

  const moduleMetadata = initialRoutingModule<C>(options);

  beforeEach(
    waitForAsync(() => {
      addMatchers(customMatchers);
      TestBed.configureTestingModule(moduleMetadata);

      overrideModules(options);

      overrideComponentIfProviderOverridesSpecified(options);

      TestBed.compileComponents();
    })
  );

  return (overrides?: SpecRoutingOverrides<C>) => {
    const defaults: SpecRoutingOverrides<C> = {
      props: {},
      detectChanges: true,
      providers: []
    };

    const { detectChanges, props, providers } = { ...defaults, ...overrides };

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    const { params, queryParams, data, fragment, url, root, parent, children, firstChild } = { ...options, ...overrides };

    TestBed.overrideProvider(ActivatedRoute, {
      useValue: new ActivatedRouteStub({ params, queryParams, data, fragment, url, root, parent, children, firstChild })
    });
    const ngZone = (<any>TestBed).inject ? TestBed.inject(NgZone) : TestBed.get(NgZone);

    return ngZone.run(() => {
      const Spec = createSpecRouting(options, props);

      Spec.router.initialNavigation();

      if (options.detectChanges && detectChanges) {
        Spec.detectChanges();
      }

      return Spec;
    });
  };
}

function createSpecRouting<C>(options: Required<SpecRoutingOptions<C>>, props?: Partial<C>): SpecRouting<C> {
  const fixture = TestBed.createComponent(options.component);
  const debugElement = fixture.debugElement;

  const component = setProps(fixture.componentInstance, props);

  /**
   * Back compatibility, angular under 9 version doesnt have a inject function
   */
  if (!TestBed.inject) {
    return new SpecRouting(fixture, debugElement, component, TestBed.get(Router), TestBed.get(ActivatedRoute));
  }

  return new SpecRouting(
    fixture,
    debugElement,
    component,
    TestBed.inject(Router),
    TestBed.inject(ActivatedRoute) as SpyObject<ActivatedRouteStub>
  );
}
